import { COLOR_THEME } from "constants/colorThemes";
import type { B2bColorScheme } from "shared-types/colorScheme";
import type ColormapInterface from "shared-types/colormapinterface";

const colorMapBam = (colors: B2bColorScheme): ColormapInterface => {
	const staffColors = {
		[COLOR_THEME.STAFF]: {
			layoutBackground: "inherit",
			passwordKeyboardBackground: colors.tertiaryBackgroundColor,
			passwordKeyboardBorder: colors.primaryInactiveColor,
			passwordKeyBackground: colors.secondaryBackgroundColor,
			passwordValidateDisabledBackground: colors.primaryInactiveColor,
			passwordValidateDisabledColor: colors.tertiaryBackgroundColor,
			passwordValidateBackground: colors.primaryHighlightColor,
			sessionDurationValueBackground: colors.secondaryBackgroundColor,
			buttonPrimary: colors.primaryHighlightColor,
			modalText: colors.tertiaryBackgroundColor,
		},
	};

	const adminColors = {
		[COLOR_THEME.ADMIN]: {
			layoutBackground: colors.black,
			passwordKeyboardBackground: colors.secondaryBackgroundColor,
			passwordKeyboardBorder: colors.primaryInactiveColor,
			passwordKeyBackground: colors.black,
			passwordValidateDisabledBackground: colors.primaryInactiveColor,
			passwordValidateDisabledColor: colors.primaryInactiveColor,
			passwordValidateBackground: colors.primaryHighlightColor,
			buttonPrimary: colors.black,
			modalText: colors.black,
		},
	};

	const searchBar = {
		searchBarBackground: colors.white,
		searchBarPlaceholder: colors.primaryInactiveColor,
		searchBarAdornment: colors.black,
		inputText: colors.tertiaryColorLight,
		inputCaret: colors.otherHighLightColor,
		noSearchResultchValidationMessage: colors.secondaryBackgroundColor,
		noSearchResultValidationContainer: colors.primaryHighlightColor,
	};

	const keyboardsAndPad = {
		keyboardBorder: colors.secondaryTextColor,
		keyboardBackground: colors.secondaryBackgroundColor,
		keyboardKeyBackground: colors.tertiaryBackgroundColor,
		keyboardKeyText: colors.white,
	};

	const waitingList = {
		waitingListSongBackground: colors.secondaryBackgroundColor,
		waitingListSongArtist: colors.white,
		waitingListDeleteConfirmationModalMessage: colors.primaryTextColor,
		waitingListDraggingOutline: colors.otherHighLightColor,
		lastSongBackground: colors.transparent,
		remainingTimeBackground: colors.otherHighLightColor,
		lastSongHeader: colors.primaryGradient,
		lastSongText: colors.primaryTextColor,
		lastSongBorder: colors.tertiaryColorLight,
		lastSongEmptyMessage: colors.tertiaryTextColor,
		sessionTimeWaitilinglistHandlerBackgroundColor: colors.primaryTextColor,
		sessionTimeWaitilinglistHandlerTextColor: colors.primaryColor,
		scrollButton: colors.white,
		scrollButtonBackground: colors.tertiaryColorLight,
		elapsedTimeICon: colors.primaryHighlightColor,
	};

	const sessionTimeHandler = {
		sessionTimeHandlerBackground: colors.secondaryBackgroundColor,
	};

	const settings = {
		switchBackground: colors.secondaryTextColor,
		switchBackgroundActivated: colors.tertiaryHighlightColor,
		switchThumb: colors.white,
		adminPanelBackground: colors.black,
		sessionDurationValueBackground: colors.secondaryBackgroundColor,
		settingsBackground: colors.black,
		volumeToggleOn: colors.tertiaryHighlightColor,
		volumeToggleOff: colors.secondaryHighlightColor,
		settingToggleContainer: colors.primaryInactiveColor,
	};

	const controlBar = {
		controlBarBackground: colors.secondaryBackgroundColor,
		playerBackground: colors.black,
		timeSliderRail: colors.primaryInactiveColor,
		volumeBarSliderRails: colors.backgroundColorLight,
		timeSliderTrackAndThumb: colors.otherHighLightColor,
		timeSliderDisabled: colors.primaryInactiveColor,
		controlBarButtonBackground: colors.transparent,
		volumeBar: colors.primaryColor,
		VolumeBarContainer: colors.white,
		playPauseBackground: colors.tertiaryColorLight,
	};

	const buttons = {
		buttonSecondary: colors.white,
		buttonDisabled: colors.primaryInactiveColor,
		buttonIconDisabled: colors.backgroundColorLight,
		buttonOutlined: colors.white,
		buttonAudioOptionDefault: colors.primaryColor,
	};

	const songList = {
		songListBorder: colors.tertiaryColorLight,
		playlistGradient1: colors.transparent,
		playlistGradient2: colors.transparent,
		playlistGradient3: colors.transparent,
		songListArtistName: colors.primaryInactiveColor,
	};

	const popoverAndModal = {
		popoverItem: colors.primaryTextColor,
		popoverItemBackground: colors.tertiaryColorLight,
		popoverItemBorder: colors.secondaryBackgroundColor,
		modalBackground: colors.tertiaryColorLight,
		modalIcon: colors.backgroundColorLight,
		languagesPopoverBackground: colors.secondaryBackgroundColor,
		languagesPopoverSelected: colors.primaryHighlightColor,
		languagesPopoverSelectedBackground: colors.secondaryBackgroundColor,
	};

	const playlists = {
		playlistInfos: colors.tertiaryTextColor,
	};

	const sessionHandler = {
		sessionHandleBorder: colors.secondaryGradient,
		prolongationValidateButton: colors.primaryHighlightColor,
		prolongationValidateButtonOk: colors.playerAccentColor3,
		prolongationOpenModalButton: colors.primaryHighlightColor,
		inactiveProlongationOpenModalButton: colors.tertiaryTextColor,
		prolongationRadioButtonBorders: colors.tertiaryColorLight,
		selectedProlongationElement: colors.primaryHighlightColor,
		unselectedProlongationElement: colors.black,
	};

	const otherProperties = {
		defaultText: colors.white,
		homeBorder: colors.secondaryBackgroundColor,
		timePanelBorder: colors.secondaryBackgroundColor,
		appBackground: colors.primaryColor,
		secondaryText: colors.white,

		highlightPoint: colors.primaryHighlightColor,

		confirmationSnackbarBackground: colors.primaryHighlightColor,
		confirmationSnackbarIcon: colors.tertiaryHighlightColor,
		controlScreenFullscreenButton: colors.white,
		desktopPlayerLauncherButtonBackground: colors.white,
		b2bSongInfos: colors.primaryTextColor,
		error: colors.secondaryHighlightColor,
		tslLogoInverted: colors.secondaryBackgroundColor,
		tslLogo: colors.white,

		playerPlaceholderBackground: colors.backgroundColor,
		swipeOptionBackgroundColor: colors.tertiaryBackgroundColor,
		mp3KbpPlayer: {
			timerCircularProgress: colors.playerAccentColor1,
			timerCircularProgressBackground: colors.transparent,
		},

		messageBlockBackground: colors.tertiaryBackgroundColor,
		messageBlockBorder: colors.secondaryTextColor,
		audioOptionButton: {
			activeBorder: colors.otherHighLightColor,
			activeBorderGlow: colors.otherHighLightColor,
		},
	};

	const lightSlider = {
		lightSliderTrack: colors.white,
		lightSliderThumb: colors.white,
		lightSliderRail: colors.white,
	};

	const gameColors = {
		gamesHighlightColor: colors.primaryHighlightColor,
	};

	const sectionsColors = [
		searchBar,
		keyboardsAndPad,
		waitingList,
		settings,
		controlBar,
		buttons,
		staffColors,
		adminColors,
		otherProperties,
		popoverAndModal,
		songList,
		sessionTimeHandler,
		gameColors,
		playlists,
		sessionHandler,
		lightSlider,
	];

	return Object.assign({}, ...sectionsColors) as ColormapInterface;
};

export default colorMapBam;
